import * as urls from "./urls";
import * as credentials from "shared/credentials";
import { UserPartial } from "model/user";
import { FollowUpBossAgentSelection, FollowUpBossAgentSelectionRequest } from "model/follow-up-boss-agent-selection";
import { UserAuth } from "model/user-auth";
import { QuotaResource } from "./resource/quota";
import { LeadResource, NewLeadResource } from "./resource/lead";
import { Lead, NewLead } from "model/lead";
import { LeadImportResource } from "./resource/lead-import";
import { LeadNote, NewLeadNote } from "model/lead-note";
import { LeadNoteResource, NewLeadNoteResource } from "./resource/lead-note";
import { LeadTag, NewLeadTag } from "model/lead-tag";
import { LeadTagResource, NewLeadTagResource } from "./resource/lead-tag";
import { LeadPhone, NewLeadPhone } from "model/lead-phone";
import { LeadPhoneResource, NewLeadPhoneResource } from "./resource/lead-phone";
import { LeadPhoneType } from "type/lead-phone";
import { NewLeadPipeline } from "model/lead-pipeline";
import { LeadPipelineResource, NewLeadPipelineResource } from "./resource/lead-pipeline";
import { SavedSearch, NewSavedSearch } from "model/saved-search";
import { SavedListing, NewSavedListing } from "model/saved-listing";
import { NewSavedListingResource, SavedListingResource } from "./resource/saved-listing";
import { NewSavedSearchResource, SavedSearchResource } from "./resource/saved-search";
import { MarketReportSubscriptionResource, NewMarketReportSubscriptionResource } from "./resource/market-report-subscription";
import { Campaign, NewCampaign } from "model/campaign";
import { CampaignResource, NewCampaignResource } from "./resource/campaign";
import { CampaignDisplayCategoryResource } from "./resource/campaign-display-category";
import { CampaignMessage, NewCampaignMessage } from "model/campaign-message";
import { CampaignRoute, NewCampaignRoute } from "model/campaign-route";
import { CampaignMessageResource, NewCampaignMessageResource } from "./resource/campaign-message";
import { CampaignCampaignMessage, NewCampaignCampaignMessage } from "model/campaign-campaign-message";
import { CampaignCampaignMessageResource, NewCampaignCampaignMessageResource } from "./resource/campaign-campaign-message";
import { CampaignSubscription, NewCampaignSubscription, NewCampaignSubscriptionBatch } from "model/campaign-subscription";
import { CampaignSubscriptionResource, NewCampaignSubscriptionResource, NewCampaignSubscriptionBatchResource } from "./resource/campaign-subscription";
import { CampaignEmailResource } from "./resource/campaign-email";
import { LeadAnniversary, NewLeadAnniversary } from "model/lead-anniversary";
import { LeadAnniversaryResource, NewLeadAnniversaryResource } from "./resource/lead-anniversary";
import { SavedSearchResultTotalResource } from "./resource/saved-search-result-total";
import { Task, NewTask } from "model/task";
import { NewTaskResource, TaskResource } from "./resource/task";
import { IntegrationPartial } from "model/integration";
import { IntegrationResource } from "./resource/integration";
import { FacebookPagesResource } from "./resource/facebook";
import { CampaignStatResource } from "./resource/campaign-stat";
import { CampaignSubscriptionStatResource } from "./resource/campaign-subscription-stat";
import { CampaignRouteResource, NewCampaignRouteResource } from "./resource/campaign-route";
import { CampaignCampaignMessageStatResource } from "./resource/campaign-campaign-message-stat";
import { CampaignCampaignMessageSendEmailResource } from "./resource/campaign-campaign-message-send-email";
import { CampaignCampaignMessageSendEmail } from "model/campaign-campaign-message-send-email";
import { CampaignsSettingsResource } from "./resource/campaigns-settings";
import { PriceRangeOptionsResource } from "./resource/price-range-options";
import { CampaignsSettings } from "model/campaigns-settings";
import { Agent, NewAgent } from "model/agent";
import { AgentResource, AgentPublicBioTestimonialResource, NewAgentResource } from "./resource/agent";
import { AgentBoard, NewAgentBoard } from "model/agent-board";
import { AgentBoardResource, NewAgentBoardResource } from "./resource/agent-board";
import { BoardOption, ClientBoardListResource, ClientBoardResource } from "./resource/client-board";
import { AgentMlsIdResource, NewAgentMlsIdResource } from "./resource/agent-mls-id";
import { NewOffice, Office } from "model/office";
import { NewOfficeResource, OfficeResource } from "./resource/office";
import { OfficeBoard, NewOfficeBoard } from "model/office-board";
import { NewOfficeBoardResource, OfficeBoardResource } from "./resource/office-board";
import { CampaignMessageType } from "type/campaign-message";
import { PageResource } from "./resource/page";
import { PagePartial } from "model/page";
import { MarketResource, NewMarketResource } from "./resource/market";
import { MarketListingResource } from "./resource/market-listing";
import { MarketReportResource } from "./resource/market-report";
import { ResellerResource } from "./resource/reseller";
import { ClientResource } from "./resource/client";
import { EmailSentResource } from "./resource/email-sent";
import {
	LeadSearchProfileResource,
	LeadConversationDayResource,
	LeadConversationMessageResource,
} from "./resource/search-agent";
import {
	LeadProfileSuggestionResource,
	LeadProfileSuggestionReferenceResource,
	LeadProfileSuggestionsActionResource,
} from "./resource/lead-profile-suggestion";
import { EmailClickResource } from "./resource/email-click";
import { LeadLastActivityResource } from "./resource/lead-last-activity";
import { BoardResource } from "./resource/boards";
import { CityResource } from "./resource/city";
import { NeighborhoodResource } from "./resource/neighborhood";
import { AggregateStatsResource } from "./resource/aggregate-stats";
import { IntegrationCalendarResource } from "./resource/integration-calendar";
import { SiteLinkResource } from "./resource/site-link";
import { LanguageResource } from "./resource/language";
import { LeadActivityStatsResource } from "./resource/lead-activity-stats";
import { LeadActivitiesResource } from "./resource/lead-activities";
import { UserResource } from "./resource/user";
import { TextingTaxIdResource } from "./resource/texting-tax-id";
import { UserAuthResource } from "./resource/user-auth";
import { NewRoundRobinRoute } from "model/round-robin-route";
import { RoundRobinRouteResource, NewRoundRobinRouteResource } from "./resource/round-robin-route";
import { PostalCodeResource } from "./resource/postal-code";
import { GeographicRoute, NewGeographicRoute } from "model/geographic-route";
import { GeographicRouteResource, NewGeographicRouteResource } from "./resource/geographic-route";
import { MarketReportSubscription, NewMarketReportSubscription } from "model/market-report-subscription";
import { Market, NewMarket } from "model/market";
import { MarketReport } from "model/market-report";
import { ListingResource } from "./resource/listing";
import { MarketSettingsResource } from "./resource/market-settings";
import { MobileSettingsResource } from "./resource/mobile-settings";
import { SearchCitiesResource } from "./resource/search-cities";
import { LocationsResource } from "./resource/locations";
import { BillingCardResource, BillingCardUpdateResource, BillingInvoiceResource, BillingResource } from "./resource/billing";
import { PropertyTypesResource } from "./resource/property-types";
import { MarketSettings } from "model/market-settings";
import { NylasTokenRequest, NylasTokenResponse } from "./resource/nylas";
import { NaturalLanguageSearchSuggestion, NewNaturalLanguageSearchSuggestion } from "model/natural-language-search-suggestion";
import { NaturalLanguageSearchSuggestionResource, NewNaturalLanguageSearchSuggestionResource } from "./resource/natural-language-search-suggestion";
import { AreaSelectorPlaceholder, NewAreaSelectorPlaceholder } from "model/area-selector-placeholder";
import { AreaSelectorPlaceholderResource, NewAreaSelectorPlaceholderResource } from "./resource/area-selector-placeholder";
import { PingResource, NewPingResource } from "./resource/ping";
import { NewPing } from "model/ping";
import { EmailRoutingRuleResource, UpsertEmailRoutingRuleResource } from "./resource/email-routing-rule";
import { EmailRecipientResource, CreateEmailRecipientResource } from "./resource/email-recipient";
import { OffMarketListingResource, UpsertOffMarketListingResource, OffMarketListingListResource } from "./resource/off-market-listing";
import { ClientPropertyResource, UpsertClientPropertyResource, ClientPropertyListResource } from "./resource/client-property";
import {
	ListingPhotoKey,
	PhotoManagerResource,
	AddListingPhotoResource,
	ReorderListingPhotosResource,
	MakeMainListingPhotoResource,
} from "./resource/listing-photo";
import { LeadContactReportResource, ContactEventTypeResource, LeadContactReportQuery } from "./resource/lead-contact-report";
import { SavedSearchActivityReportResource, SavedSearchActivityQuery } from "./resource/saved-search-activity";
import { SavedListingReportResource } from "./resource/saved-listing-report";
import { MobileActivityReportResponse, MobileActivityQuery } from "./resource/mobile-activity";
import { EmailBrandingResource } from "./resource/email-branding";
import { ActiveListingResource } from "./resource/active-listing";
import { ActiveListingModifyResource, UpdateActiveListingModifyResource } from "./resource/active-listing-modify";
import { saveBlob } from "shared/save-blob";


export const createPing = (ping: NewPing) => {
	const body: NewPingResource = {
		...ping,
	};
	return request<PingResource, NewPingResource>({
		url: urls.ping(),
		method: "POST",
		body,
	});
}

export const getUser = () => {
    return requestLegacy<UserResource>({
        url: urls.user(),
        method: "GET",
    });
}

export const profileSync = () => {
    return requestLegacy({
        url: urls.profileSync(),
        method: "POST",
        body: {},
    });
}

export const saveUser = (user: UserPartial) => {
    const body = {
        ...user,
        ...(user.kestrelVersion && {kestrelVersion: user.kestrelVersion.id}),
        ...(user.websitePlatform && {websitePlatform: user.websitePlatform.id}),
        ...(user.listingDetailSidebarPosition && {listingDetailSidebarPosition: user.listingDetailSidebarPosition.id}),
        ...(user.settingListingDetailPageStyle && {settingListingDetailPageStyle: user.settingListingDetailPageStyle.id}),
        ...(user.leadCaptureType && {leadCaptureType: user.leadCaptureType.id}),
        ...(user.searchPagePromptType && {searchPagePromptType: user.searchPagePromptType.id}),
        ...(user.searchResultsPromptType && {searchResultsPromptType: user.searchResultsPromptType.id}),
        ...(user.searchDetailPromptType && {searchDetailPromptType: user.searchDetailPromptType.id}),
        ...(user.mapPosition && {mapPosition: user.mapPosition.id}),
		...(user.agentSortOrder && {agentSortOrder: user.agentSortOrder.id}),
		...(user.officeSortOrder && {officeSortOrder: user.officeSortOrder.id}),
		...(user.textMessageBrandingStatus && {textMessageBrandingStatus: user.textMessageBrandingStatus.id}),
		textMessageRegistrationStatus: undefined,
		textMessageTaxIdDefaultType: undefined,
		textMessageTaxIdMasked: undefined,
        ...(user.activeResultsSort && {activeResultsSortId: user.activeResultsSort.id}),
        ...(user.eurekaResultsSort && {eurekaResultsSortId: user.eurekaResultsSort.id}),
        ...(user.openHouseDisplayType && {openHouseDisplayTypeId: user.openHouseDisplayType.id}),
        ...(user.openHouseSearchType && {openHouseSearchTypeId: user.openHouseSearchType.id}),
        ...(user.limitSoldResultsByMonths && {limitSoldResultsByMonthsId: user.limitSoldResultsByMonths.id}),
    };
    return requestLegacy({
        url: urls.user(),
        method: "PUT",
        body
    });
}

export const getPriceRangeOptions = () => {
    return requestLegacy<PriceRangeOptionsResource>({
        url: urls.priceRangeOptions(),
        method: "GET",
    });
}

export const savePriceRangeOptions = (options: PriceRangeOptionsResource) => {
    return requestLegacy<PriceRangeOptionsResource>({
        url: urls.priceRangeOptions(),
        method: "PUT",
        body: options,
    });
}

export const saveUserAuth = (userAuth: UserAuth) => {
    const body = {
        ...userAuth
    }
    return requestLegacy<UserAuthResource>({
        url: urls.userAuth(),
        method: "PUT",
        body
    })
}

export const getAdminMode = () => {
    const url = urls.adminMode();
    return new Promise<boolean>((resolve, reject) => {
        fetch(url, {
            method: "GET",
            credentials: "include",
        })
        .then((response) => {
            response.json().then((json) => {
                resolve(json);
            });
        })
        .catch((error) => {
            reject(error);
        })
        ;
    });
}

export const getResellerById = (id: number) => {
    return requestLegacy<ResellerResource>({
        url: urls.reseller(id),
        method: "GET",
    });
}

export const getClient = (id: number) => {
    return requestLegacy<ClientResource>({
        url: urls.client(id),
        method: "GET",
    });
}

export const getQuota = () => {
    return requestLegacy<QuotaResource>({
        url: urls.quota(),
        method: "GET",
    });
}

export const getLeads = () => {
    return requestLegacy<LeadResource[]>({
        url: urls.leads(),
        method: "GET",
    });
}

export const createLead = (lead: NewLead) => {
	const body: NewLeadResource = {
		name: lead.name,
		email: lead.email,
		email2: lead.email2,
		phone: lead.phone,
		address: lead.address,
		city: lead.city,
		state: lead.state,
		zip: lead.zip,
		status: lead.status && lead.status.id,
		note: lead.note,
		agentId:
			lead.agentId !== undefined
				? lead.agentId
				: lead.agent
					? lead.agent.id
					: undefined,
		readiness: lead.readiness ? lead.readiness : undefined,
		transactionTimelineStart: lead.transactionTimelineStart
			? lead.transactionTimelineStart
			: undefined,
		transactionTimelineEnd: lead.transactionTimelineEnd
			? lead.transactionTimelineEnd
			: undefined,
		justBrowsing: lead.justBrowsing,
		transactionPrice: lead.transactionPrice ?? undefined,
		workingWithAnotherAgent: lead.workingWithAnotherAgent,
		mortgagePreApproval: lead.mortgagePreApproval,
		referralRequested: lead.referralRequested,
	};
	return request<LeadResource, NewLeadResource>({
		url: urls.leads(),
		method: "POST",
		body,
	});
}

export const updateLead = (lead: Lead) => {
	const body: LeadResource = {
		id: lead.id,
		name: lead.name,
		email: lead.email,
		email2: lead.email2,
		phone: lead.phone,
		address: lead.address,
		city: lead.city,
		state: lead.state,
		zip: lead.zip,
		status: lead.status && lead.status.id,
		note: lead.note,
		agentId:
			lead.agentId !== undefined
				? lead.agentId
				: lead.agent
					? lead.agent.id
					: undefined,
		readiness: lead.readiness ? lead.readiness : undefined,
		transactionTimelineStart: lead.transactionTimelineStart
			? lead.transactionTimelineStart
			: undefined,
		transactionTimelineEnd: lead.transactionTimelineEnd
			? lead.transactionTimelineEnd
			: undefined,
		justBrowsing: lead.justBrowsing,
		transactionPrice: lead.transactionPrice ?? undefined,
		workingWithAnotherAgent: lead.workingWithAnotherAgent,
		mortgagePreApproval: lead.mortgagePreApproval,
		referralRequested: lead.referralRequested,
	};
	return request<LeadResource, LeadResource>({
		url: urls.lead(lead.id),
		method: "PUT",
		body,
	});
}

export const getSavedListings = (leadId: number) => {
    return requestLegacy<SavedListingResource[]>({
        url: urls.savedListings(leadId),
        method: "GET",
    });
}

export const createSavedListing = (savedListing: NewSavedListing) => {
	const body: NewSavedListingResource = {
		leadId: savedListing.lead && savedListing.lead.id,
		rating: savedListing.rating,
		comments: savedListing.comments,
		deleted: savedListing.deleted ? savedListing.deleted : false,
		// @ts-ignore
		listing: savedListing.listing,
	};

	return request<SavedListingResource, NewSavedListingResource>({
		url: urls.savedListings(),
		method: "POST",
		body,
	});
}

export const updateSavedListing = (savedListing: SavedListing) => {
	let listing: ListingResource | undefined = savedListing.listing ? {
		id: savedListing.listing.id,
		listingNumber: savedListing.listing.listingNumber,
		boardId: savedListing.listing.boardId,
		propertyType: savedListing.listing.propertyType?.id || "",
		address: savedListing.listing.address.join(" "),
	} : undefined;
	const body: SavedListingResource = {
		id: savedListing.id,
		leadId: savedListing.lead && savedListing.lead.id,
		deleted: savedListing.deleted ? savedListing.deleted : false,
		rating: savedListing.rating ? savedListing.rating : undefined,
		comments: savedListing.comments ? savedListing.comments : undefined,
		createdOn: savedListing.createdOn ? savedListing.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : undefined,
		modifiedOn: savedListing.modifiedOn ? savedListing.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : undefined,
		listing: listing ? listing : undefined,
	};
	return request<SavedListingResource, SavedListingResource>({
		url: urls.savedListing(savedListing.id),
		method: "PUT",
		body,
	});
}

export const getSavedSearches = () => {
    return requestLegacy<SavedSearchResource[]>({
        url: urls.savedSearches(),
        method: "GET",
    });
}

export const createSavedSearch = (savedSearch: NewSavedSearch) => {
	const body: NewSavedSearchResource = {
		leadId: savedSearch.leadId,
		name: savedSearch.name,
		recieveEmails: savedSearch.recieveEmails,
		comments: savedSearch.comments,
		inactive: savedSearch.inactive,
		criteria: savedSearch.criteria,
	};
	return request<SavedSearchResource, NewSavedSearchResource>({
		url: urls.savedSearches(),
		method: "POST",
		body,
	});
}

export const updateSavedSearch = (savedSearch: SavedSearch) => {
	const body: SavedSearchResource = {
		id: savedSearch.id,
		leadId: savedSearch.leadId,
		name: savedSearch.name,
		recieveEmails: savedSearch.recieveEmails,
		comments: savedSearch.comments,
		inactive: savedSearch.inactive,
		createdOn: savedSearch.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		modifiedOn: savedSearch.modifiedOn ? savedSearch.modifiedOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : undefined, 
		criteria: savedSearch.criteria,
		resultsUrl: savedSearch.resultsUrl,
	};
	return request<SavedSearchResource, SavedSearchResource>({
		url: urls.savedSearch(savedSearch.id),
		method: "PUT",
		body,
	});
}

export const getSavedSearchResultTotal = (savedSearchId: number) => {
    return requestLegacy<SavedSearchResultTotalResource[]>({
        url: urls.savedSearchResultTotal(savedSearchId),
        method: "GET",
    });
}

/**
 * Resolves a resource with empty lists when the lead has no generated profile, and null
 * only when the lead is not accessible to the caller.
 */
export const getLeadSearchProfile = (leadId: number) => {
    return requestLegacy<LeadSearchProfileResource | null>({
        url: urls.leadSearchProfile(leadId),
        method: "GET",
    });
}

/**
 * Permanently deletes the lead's generated search profile so the Search Agent starts fresh.
 * The conversation history is kept. Resolves with no body (204).
 */
export const deleteLeadSearchProfileHistory = (leadId: number) => {
    return request<void>({
        url: urls.leadSearchProfile(leadId),
        method: "DELETE",
    });
}

export const getLeadConversationHistory = (leadId: number) => {
    return requestLegacy<LeadConversationDayResource[] | null>({
        url: urls.leadConversationHistory(leadId),
        method: "GET",
    });
}

export const getLeadConversationHistoryDay = (leadId: number, date: string) => {
    return requestLegacy<LeadConversationMessageResource[] | null>({
        url: urls.leadConversationHistoryDay(leadId, date),
        method: "GET",
    });
}

export const getLeadProfileSuggestions = (leadId: number) => {
    return request<LeadProfileSuggestionResource[]>({
        url: urls.leadProfileSuggestions(leadId),
        method: "GET",
    });
}

/** Applies the suggestions to the lead and resolves with the updated lead. */
export const acceptLeadProfileSuggestions = (leadId: number, suggestions: LeadProfileSuggestionReferenceResource[]) => {
    const body: LeadProfileSuggestionsActionResource = {
        leadId,
        suggestions,
    };
    return request<LeadResource, LeadProfileSuggestionsActionResource>({
        url: urls.leadProfileSuggestionsAccept(),
        method: "POST",
        body,
    });
}

export const dismissLeadProfileSuggestions = (leadId: number, suggestions: LeadProfileSuggestionReferenceResource[]) => {
    const body: LeadProfileSuggestionsActionResource = {
        leadId,
        suggestions,
    };
    return request<void, LeadProfileSuggestionsActionResource>({
        url: urls.leadProfileSuggestionsDismiss(),
        method: "POST",
        body,
    });
}

export const getEmailSents = (leadId: number) => {
    return requestLegacy<EmailSentResource[]>({
        url: urls.emailSents(leadId),
        method: "GET",
    });
}

export const getEmailClicks = (leadId: number) => {
    return requestLegacy<EmailClickResource[]>({
        url: urls.emailClicks(leadId),
        method: "GET",
    });
}

export const getLeadPhones = () => {
    return requestLegacy<LeadPhoneResource[]>({
        url: urls.leadPhones(),
        method: "GET"
    });
}

export const createLeadPhone = (leadPhone: NewLeadPhone) => {
	const body: NewLeadPhoneResource = {
		leadId: leadPhone.leadId,
		typeId: leadPhone.type?.id ?? LeadPhoneType.MOBILE.id,
		value: leadPhone.value,
		deleted: leadPhone.deleted
	};
	return request<LeadPhoneResource, NewLeadPhoneResource>({
		url: urls.leadPhones(),
		method: "POST",
		body
	});
}

export const updateLeadPhone = (leadPhone: LeadPhone) => {
	const body: LeadPhoneResource = {
		id: leadPhone.id,
		leadId: leadPhone.leadId,
		typeId: leadPhone.type?.id ?? LeadPhoneType.MOBILE.id,
		value: leadPhone.value,
		deleted: leadPhone.deleted,
		createdOn: leadPhone.createdOn.utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		updatedOn: leadPhone.updatedOn.utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
	};
	return request<LeadPhoneResource, LeadPhoneResource>({
		url: urls.leadPhone(leadPhone.id),
		method: "PUT",
		body
	});
}

export const getLeadPipelines = () => {
    return requestLegacy<LeadPipelineResource[]>({
        url: urls.leadPipelines(),
        method: "GET"
    })
}

export const createLeadPipeline = (leadPipeline: NewLeadPipeline) => {
    const body: NewLeadPipelineResource = {
        leadId: leadPipeline.leadId,
        typeId: leadPipeline.type && leadPipeline.type.id,
    }
    return request<LeadPipelineResource, NewLeadPipelineResource>({
        url: urls.leadPipelines(),
        method: "POST",
        body
    })
}

export const getLeadAnniversaries = () => {
    return requestLegacy<LeadAnniversaryResource[]>({
        url: urls.leadAnniversaries(),
        method: "GET"
    });
}

export const createLeadAnniversary = (leadAnniversary: NewLeadAnniversary) => {
	const body: NewLeadAnniversaryResource = {
		leadId: leadAnniversary.lead && leadAnniversary.lead.id,
		typeId: leadAnniversary.type && leadAnniversary.type.id,
		startDate: leadAnniversary.startDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		deleted: leadAnniversary.deleted ? leadAnniversary.deleted : false,
	};
	return request<LeadAnniversaryResource, NewLeadAnniversaryResource>({
		url: urls.leadAnniversaries(),
		method: "POST",
		body
	});
};

export const updateLeadAnniversary = (leadAnniversary: LeadAnniversary) => {
	const body: LeadAnniversaryResource = {
		id: leadAnniversary.id,
		leadId: leadAnniversary.lead && leadAnniversary.lead.id,
		typeId: leadAnniversary.type && leadAnniversary.type.id,
		startDate: leadAnniversary.startDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		createdOn: leadAnniversary.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		updatedOn: leadAnniversary.updatedOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		deleted: leadAnniversary.deleted ? leadAnniversary.deleted : false,
	};
	return request<LeadAnniversaryResource, LeadAnniversaryResource>({
		url: urls.leadAnniversary(leadAnniversary.id),
		method: "PUT",
		body,
	});
}

export const getLeadNotes = () => {
    return requestLegacy<LeadNoteResource[]>({
        url: urls.leadNotes(),
        method: "GET"
    });
}

export const saveLeadImport = (leadImportResources: LeadImportResource[]) => {
    const body = leadImportResources;
    return requestLegacy<LeadImportResource[]>({
        url: urls.leadImports(),
        method: "POST",
        body,
    });
}

export const createLeadNote = (leadNote: NewLeadNote) => {
	const text = (leadNote.text) ? leadNote.text : "";
	const body: NewLeadNoteResource = {
        leadId: leadNote.leadId,
        typeId: leadNote.type && leadNote.type.id,
        text,
        deleted: leadNote.deleted
    };
	return request<LeadNoteResource, NewLeadNoteResource>({
		url: urls.leadNotes(),
		method: "POST",
		body,
	});
}

export const updateLeadNote = (leadNote: LeadNote) => {
	const text = (leadNote.text) ? leadNote.text : "";
	const body: LeadNoteResource = {
		...leadNote,
        leadId: leadNote.leadId,
        typeId: leadNote.type && leadNote.type.id,
        text,
        deleted: leadNote.deleted,
		updatedOn: leadNote.updatedOn.utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		createdOn: leadNote.createdOn.utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    };
	return request<LeadNoteResource, LeadNoteResource>({
		url: urls.leadNote(leadNote.id),
		method: "PUT",
		body,
	});
};

export const getLeadTags = () => {
    return requestLegacy<LeadTagResource[]>({
        url: urls.leadTags(),
        method: "GET"
    });
}

export const createLeadTag = (leadTag: NewLeadTag) => {
    const body: NewLeadTagResource = {
		leadId: leadTag.leadId,
		text: leadTag.text,
	};
    return request<LeadTagResource, NewLeadTagResource>({
        url: urls.leadTags(),
        method: "POST",
        body,
    });
}

export const deleteLeadTag = (leadTag: LeadTag) => {
    return requestLegacy<LeadTagResource>({
        url: urls.leadTag(leadTag.id || 0),
        method: "DELETE",
    });
}

export const getLeadActivities = (leadId?: number) => {
    return requestLegacy<LeadActivitiesResource[]>({
        url: urls.leadActivities(leadId),
        method: "GET",
    });
}

export const getLeadActivityStats = () => {
    return requestLegacy<LeadActivityStatsResource[]>({
        url: urls.leadActivityStats(),
        method: "GET",
    });
}

export const getLeadLastActivities = () => {
    return requestLegacy<LeadLastActivityResource[]>({
        url: urls.leadLastActivities(),
        method: "GET",
    });
}

export const getLeadLastSearchAgentChats = () => {
    return requestLegacy<LeadLastActivityResource[]>({
        url: urls.leadLastSearchAgentChats(),
        method: "GET",
    });
}

export const getGeographicRoutes = () => {
	return request<GeographicRouteResource[]>({
		url: urls.geographicRoutes(),
		method: "GET",
	});
}

export const createGeographicRoute = (geographicRoute: NewGeographicRoute) => {
	const body: NewGeographicRouteResource = {
		agentId: geographicRoute.agent.id,
		postalCode: geographicRoute.postalCode,
	};
	return request<GeographicRouteResource, NewGeographicRouteResource>({
		url: urls.geographicRoutes(),
		method: "POST",
		body,
	});
};

export const deleteGeographicRoute = (geographicRoute: GeographicRoute) => {
		return request<GeographicRouteResource>({
			url: urls.geographicRoute(geographicRoute.id),
			method: "DELETE",
		});
};

export const getRoundRobinRoutes = () => {
	return request<RoundRobinRouteResource[]>({
		url: urls.roundRobinRoutes(),
		method: "GET",
	});
}

export const updateRoundRobinRoutes = (roundRobinRoutes: NewRoundRobinRoute[]) => {
	const body: NewRoundRobinRouteResource[] = [];
	roundRobinRoutes.forEach(roundRobinRoute => {
		body.push({
			agentId: roundRobinRoute.agent.id,
		});
	})
	return request<RoundRobinRouteResource[], NewRoundRobinRouteResource[]>({
		url: urls.roundRobinRoutes(),
		method: "PUT",
		body,
	});
}

export const getPostalCodes = () => {
	return request<PostalCodeResource[]>({
		url: urls.postalCodesRoutes(),
		method: "GET",
	});
}


export const createCampaign = (campaign: NewCampaign) => {
	const body: NewCampaignResource = {
		name: campaign.name,
		// MAX-3879: without this, a newly created campaign's initial active/inactive state (the
		// "Set as active" toggle) is silently dropped and the server default wins.
		status: campaign.status && campaign.status.id,
		description: campaign.description,
		campaignType: campaign.campaignType ? campaign.campaignType.id : null,
		marketId: campaign.marketId,
		emailBrandingYn: campaign.emailBrandingYn,
		emailMarketingTypeId: campaign.emailMarketingType.id,
		campaignCategoryId: campaign.campaignCategoryId,
		// MAX-3878: new per-account display category (distinct from the global campaignCategoryId).
		// null = Uncategorized; server treats an absent key as "don't change", so send explicitly.
		campaignDisplayCategoryId: campaign.campaignDisplayCategoryId,
		inactiveYn: campaign.inactiveYn,
	};
	return request<CampaignResource, NewCampaignResource>({
		url: urls.campaigns(),
		method: "POST",
		body,
	});
};

export const getCampaigns = () => {
    return requestLegacy<CampaignResource[]>({
        url: urls.campaigns(),
        method: "GET",
    });
}

export const getCampaignDisplayCategories = () => {
    return requestLegacy<CampaignDisplayCategoryResource[]>({
        url: urls.campaignDisplayCategories(),
        method: "GET",
    });
}

export const createCampaignDisplayCategory = (name: string) => {
    return request<CampaignDisplayCategoryResource, { name: string }>({
        url: urls.campaignDisplayCategories(),
        method: "POST",
        body: { name },
    });
}

export const updateCampaignDisplayCategory = (id: number, name: string) => {
    return request<CampaignDisplayCategoryResource, { name: string }>({
        url: urls.campaignDisplayCategory(id),
        method: "PUT",
        body: { name },
    });
}

// Delete (empty) or Move-&-Delete (moveToId reassigns campaigns first — one atomic server op, AR4).
// moveToId null/absent ⇒ campaigns fall to Uncategorized. Server returns 409 for the last real
// category; the client also disables that action (last-category guard on both sides).
export const deleteCampaignDisplayCategory = (id: number, moveToId?: number | null) => {
    return requestLegacy<void>({
        url: urls.campaignDisplayCategory(id, moveToId),
        method: "DELETE",
    });
}

export const updateCampaign = (campaign: Campaign) => {
	const body: CampaignResource = {
		id: campaign.id,
		name: campaign.name,
		status: campaign.status && campaign.status.id,
		description: campaign.description,
		// MAX-3855: campaignType must be sent as explicit null (not omitted) when "No Smart Content" is
		// selected, so ihf-root's PATCH-style deserializer (which treats an absent key as "don't change
		// this field") actually clears the prior value instead of leaving it untouched.
		campaignType: campaign.campaignType ? campaign.campaignType.id : null,
		marketId: campaign.marketId,
		emailBrandingYn: campaign.emailBrandingYn,
		emailMarketingTypeId: campaign.emailMarketingType.id,
		campaignCategoryId: campaign.campaignCategoryId,
		// MAX-3878: new per-account display category (distinct from the global campaignCategoryId).
		// null = Uncategorized; sent explicitly so a move/clear round-trips (server treats an
		// absent key as "don't change this field").
		campaignDisplayCategoryId: campaign.campaignDisplayCategoryId,
		inactiveYn: campaign.inactiveYn,
		dateCreated: campaign.dateCreated.format("YYYY-MM-DDTHH:mm:ss.SSS"),
	};
	return request<CampaignResource, CampaignResource>({
		url: urls.campaign(campaign.id),
		method: "PUT",
		body,
	});
};

export const getCampaignMessages = () => {
    return requestLegacy<CampaignMessageResource[]>({
        url: urls.campaignMessages(),
        method: "GET",
    });
}

export const resendTextingOtpVerification = () => {
	return request<string>({
		url: urls.campaignTextingResendOtp(),
		method: "POST"
	});
}

export const getTextingTaxId = () => {
	return request<TextingTaxIdResource>({
		url: urls.campaignTextingTaxId(),
		method: "GET"
	});
}

export const createCampaignMessage = (campaignMessage: NewCampaignMessage) => {
	const attachments = (campaignMessage.attachments) ? campaignMessage.attachments : [];
	const body: NewCampaignMessageResource = {
		name: campaignMessage.name,
		subject: campaignMessage.subject,
		htmlContent: campaignMessage.htmlContent,
		jsonContent: campaignMessage.jsonContent,
		messageType: campaignMessage.messageType ? campaignMessage.messageType.id : CampaignMessageType.EMAIL.id,
		attachments,
		timeToSendNumber: campaignMessage.timeToSendNumber,
		timeToSendUnit: campaignMessage.timeToSendUnit && campaignMessage.timeToSendUnit.id,
		status: campaignMessage.status && campaignMessage.status.id,
		isInactiveYn: campaignMessage.isInactiveYn,
		ownedByReseller: campaignMessage.ownedByReseller,
		blast: campaignMessage.blast,
	};
	return request<CampaignMessageResource, NewCampaignMessageResource>({
		url: urls.campaignMessages(),
		method: "POST",
		body,
	});
}

export const updateCampaignMessage = (campaignMessage: CampaignMessage) => {
	const body: CampaignMessageResource = {
		id: campaignMessage.id,
		name: campaignMessage.name,
		subject: campaignMessage.subject,
		htmlContent: campaignMessage.htmlContent,
		jsonContent: campaignMessage.jsonContent,
		messageType: campaignMessage.messageType ? campaignMessage.messageType.id : CampaignMessageType.EMAIL.id,
		attachments: campaignMessage.attachments,
		timeToSendNumber: campaignMessage.timeToSendNumber,
		timeToSendUnit: campaignMessage.timeToSendUnit && campaignMessage.timeToSendUnit.id,
		status: campaignMessage.status && campaignMessage.status.id,
		isInactiveYn: campaignMessage.isInactiveYn,
		ownedByReseller: false,
		dateCreated: "",
		blast: campaignMessage.blast,
	};
	return request<CampaignMessageResource, CampaignMessageResource>({
		url: urls.campaignMessage(campaignMessage.id),
		method: "PUT",
		body,
	});
};	

export const deleteCampaignMessage = (campaignMessage: CampaignMessage) => {
    return requestLegacy<CampaignMessageResource>({
        url: urls.campaignMessage(campaignMessage.id || 0),
        method: "PUT",
    });
}

export const getCampaignCampaignMessages = () => {
    return requestLegacy<CampaignCampaignMessageResource[]>({
        url: urls.campaignCampaignMessages(),
        method: "GET",
    });
}

export const createCampaignCampaignMessage = (campaignCampaignMessage: NewCampaignCampaignMessage) => {
	const body: NewCampaignCampaignMessageResource = {
		campaignId: campaignCampaignMessage.campaignId,
		campaignMessageId: campaignCampaignMessage.campaignMessageId,
		messageOrder: campaignCampaignMessage.messageOrder,
	};
	return request<CampaignCampaignMessageResource, NewCampaignCampaignMessageResource>({
		url: urls.campaignCampaignMessages(),
		method: "POST",
		body,
	});
};

export const updateCampaignCampaignMessage = (campaignCampaignMessage: CampaignCampaignMessage) => {
	const body: CampaignCampaignMessageResource = {
		id: campaignCampaignMessage.id,
		campaignId: campaignCampaignMessage.campaignId,
		campaignMessageId: campaignCampaignMessage.campaignMessageId,
		messageOrder: campaignCampaignMessage.messageOrder,
		dateCreated: campaignCampaignMessage.dateCreated.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
	};
	return request<CampaignCampaignMessageResource, CampaignCampaignMessageResource>({
		url: urls.campaignCampaignMessage(campaignCampaignMessage.id),
		method: "PUT",
		body,
	});
};

export const updateCampaignCampaignMessages = (campaignCampaignMessages: CampaignCampaignMessage[]) => {
    const body: CampaignCampaignMessageResource[] = campaignCampaignMessages.map((campaignCampaignMessage) => (
        {
            id: campaignCampaignMessage.id,
            campaignId: campaignCampaignMessage.campaignId,
            campaignMessageId: campaignCampaignMessage.campaignMessageId,
            messageOrder: campaignCampaignMessage.messageOrder,
			dateCreated: campaignCampaignMessage.dateCreated.format("YYYY-MM-DDTHH:mm:ss.SSS"),
        }
    ));
    return request<CampaignCampaignMessageResource[], CampaignCampaignMessageResource[]>({
        url: urls.campaignCampaignMessages(),
        method: "PUT",
        body,
    });
}

export const sendCampaignCampagaignMessageEmailTest = (campaignCampaignMessageSendEmail: CampaignCampaignMessageSendEmail) => {
    const body: CampaignCampaignMessageSendEmailResource = {
        campaignCampaignMessageId: campaignCampaignMessageSendEmail.campaignCampaignMessage.id,
        leadId: campaignCampaignMessageSendEmail.lead.id,
    }
    return requestLegacy<CampaignCampaignMessageSendEmailResource>({
        url: urls.campaignCampaignMessageSendTestEmail(),
        method: "POST",
        body,
    });
}

export const getCampaignSubscriptions = () => {
    return requestLegacy<CampaignSubscriptionResource[]>({
        url: urls.campaignSubscriptions(),
        method: "GET",
    });
};

export const createCampaignSubscription = (campaignSubscription: NewCampaignSubscription) => {
	const body: NewCampaignSubscriptionResource = {
		campaignId: campaignSubscription.campaign.id,
		leadId: campaignSubscription.lead.id,
		status: campaignSubscription.status.id,
	};
	return request<CampaignSubscriptionResource, NewCampaignSubscriptionResource>({
		url: urls.campaignSubscriptions(),
		method: "POST",
		body,
	});
};

export const createCampaignSubscriptionsBatch = (batch: NewCampaignSubscriptionBatch) => {
	const body: NewCampaignSubscriptionBatchResource = {
		campaignId: batch.campaign.id,
		leadIds: batch.leadIds,
		status: batch.status.id,
	};
	return request<NewCampaignSubscriptionBatchResource, NewCampaignSubscriptionBatchResource>({
		url: urls.campaignSubscriptionsBatch(),
		method: "POST",
		body,
	});
};

export const updateCampaignSubscription = (campaignSubscription: CampaignSubscription) => {
	const body: CampaignSubscriptionResource = {
		id: campaignSubscription.id,
		campaignId: campaignSubscription.campaign.id,
		leadId: campaignSubscription.lead.id,
		status: campaignSubscription.status.id,
		createdOn: campaignSubscription.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
	};
	return request<CampaignSubscriptionResource, CampaignSubscriptionResource>({
		url: urls.campaignSubscription(campaignSubscription.id),
		method: "PUT",
		body,
	});
};

export const getCampaignEmails = (leadId?: number, campaignId?: number, blast?: boolean) => {
    return requestLegacy<CampaignEmailResource[]>({
        url: urls.campaignEmails(leadId, campaignId, blast),
        method: "GET",
    });
}

export const getCampaignStats = () => {
        return requestLegacy<CampaignStatResource[]>({
            url: urls.campaignStats(),
            method: "GET",
        });
}

export const getCampaignSubscriptionStats = () => {
    return requestLegacy<CampaignSubscriptionStatResource[]>({
        url: urls.campaignSubscriptionStats(),
        method: "GET",
    });
}

export const getCampaignCampaignMessageStats = () => {
    return requestLegacy<CampaignCampaignMessageStatResource[]>({
        url: urls.campaignCampaignMessageStats(),
        method: "GET",
    });
}

export const getCampaignRoutes = () => {
    return requestLegacy<CampaignRouteResource[]>({
        url: urls.campaignRoutes(),
        method: "GET",
    });
}

export const createCampaignRoute = (campaignRoute: NewCampaignRoute) => {
	// MAX-3857: always resolved by AddRoute's handleSave before this runs.
	const body: NewCampaignRouteResource = {
		campaignId: campaignRoute.campaign!.id,
		triggers: campaignRoute.triggers,
		enrollmentMode: campaignRoute.enrollmentMode,
		reEngageDays: campaignRoute.reEngageDays,
		inactive: campaignRoute.inactive,
		leadSource: campaignRoute.leadSource,
	};
	return request<CampaignRouteResource, NewCampaignRouteResource>({
		url: urls.campaignRoutes(),
		method: "POST",
		body,
	});
};

export const updateCampaignRoute = (campaignRoute: CampaignRoute) => {
	// MAX-3857: unlike createCampaignRoute, this is called from more than one place
	// (including toggling a route's Active switch), so the campaign isn't always
	// guaranteed by a single caller -- fail loudly instead of asserting.
	if (!campaignRoute.campaign) {
		throw new Error("updateCampaignRoute requires a campaign");
	}
	// MAX-3873: enrollmentCount is output-only — never echo it back on update (contract).
	const body: Omit<CampaignRouteResource, "enrollmentCount"> = {
		id: campaignRoute.id,
		campaignId: campaignRoute.campaign.id,
		triggers: campaignRoute.triggers,
		enrollmentMode: campaignRoute.enrollmentMode,
		reEngageDays: campaignRoute.reEngageDays,
		inactive: campaignRoute.inactive,
		leadSource: campaignRoute.leadSource,
	};
	return request<CampaignRouteResource, Omit<CampaignRouteResource, "enrollmentCount">>({
		url: urls.campaignRoute(campaignRoute.id),
		method: "PUT",
		body,
	});
};

export const deleteCampaignRoute = (campaignRoute: CampaignRoute) => {
    // MAX-3857: no body needed -- the DELETE endpoint has no @RequestBody, matching deleteAreaSelectorPlaceholder's pattern.
    return requestLegacy<CampaignRouteResource>({
        url: urls.campaignRoute(campaignRoute.id),
        method: "DELETE",
    });
}

export const getCampaignsSettings = () => {
    return requestLegacy<CampaignsSettingsResource>({
        url: urls.campaingsSettings(),
        method: "GET",
    });
}

export const updateCampaignsSettings = (campaignsSettings: CampaignsSettings) => {
    const body: CampaignsSettingsResource = {}
    if (campaignsSettings.dynamicContentAddress) {
        body.dynamicContentAddress = campaignsSettings.dynamicContentAddress
    }
    if (campaignsSettings.lead) {
        body.leadId = campaignsSettings.lead.id
    }
    return requestLegacy<CampaignsSettingsResource>({
        url: urls.campaingsSettings(),
        method: "PUT",
        body,
    });
}

export const createMarket = (market: NewMarket) => {
	const body: NewMarketResource = {
		name: market.name,
		description: market.description,
		criteria: market.criteria,
		inactive: market.inactive ? market.inactive : false,
		promotedInAreaSelector: market.promotedInAreaSelector ? market.promotedInAreaSelector : false,
	}
    return request<MarketResource, NewMarketResource>({
        url: urls.markets(),
        method: "POST",
        body,
    });
}

export const updateMarket = (market: Market) => {
	const body: MarketResource = {
		id: market.id,
		name: market.name,
		description: market.description,
		criteria: market.criteria,
		header: market.header,
		footer: market.footer,
		subhead: market.subhead,
		showOnIndex: market.showOnIndex,
		sortOrder: market.sortOrder,
		photoUrl: market.photoUrl,
		inactive: market.inactive ? market.inactive : false,
		promotedInAreaSelector: market.promotedInAreaSelector,
	}
    return requestLegacy<MarketResource>({
        url: urls.market(body.id),
        method: "PUT",
        body,
    });
}

export const getMarkets = () => {
    return requestLegacy<MarketResource[]>({
        url: urls.markets(),
        method: "GET",
    });
}

export const getMarketSettings = () => {
	return requestLegacy<MarketSettingsResource>({
		url: urls.marketSettings(),
		method: "GET",
	});
}

export const updateMarketSettings = (marketSettings: MarketSettings) => {
	const body: MarketSettingsResource = {
		listingCarouselColumns: marketSettings.listingCarouselColumns,
		communitiesLayout: marketSettings.communitiesLayout
	}

	return requestLegacy<MarketSettingsResource>({
		url: urls.marketSettings(),
		method: "PUT",
	body,
	});
}

export const getMobileSettings = () => {
	return requestLegacy<MobileSettingsResource>({
		url: urls.mobileSettings(),
		method: "GET",
	});
}

export const updateMobileSettings = (body: MobileSettingsResource) => {
	return requestLegacy<MobileSettingsResource>({
		url: urls.mobileSettings(),
		method: "PUT",
		body,
	});
}

export const getSearchCities = () => {
	return requestLegacy<SearchCitiesResource>({
		url: urls.searchCities(),
		method: "GET",
	});
}

export const updateSearchCities = (body: SearchCitiesResource) => {
	return requestLegacy<SearchCitiesResource>({
		url: urls.searchCities(),
		method: "PUT",
		body,
	});
}

export const getLocations = () => {
	return requestLegacy<LocationsResource>({
		url: urls.locations(),
		method: "GET",
	});
}

export const updateLocations = (body: LocationsResource) => {
	return requestLegacy<LocationsResource>({
		url: urls.locations(),
		method: "PUT",
		body,
	});
}

export const getPropertyTypes = () => {
	return requestLegacy<PropertyTypesResource>({
		url: urls.propertyTypes(),
		method: "GET",
	});
}

export const updatePropertyTypes = (body: PropertyTypesResource) => {
	return requestLegacy<PropertyTypesResource>({
		url: urls.propertyTypes(),
		method: "PUT",
		body,
	});
}

export const restorePropertyTypes = () => {
	return requestLegacy<PropertyTypesResource>({
		url: urls.propertyTypesRestore(),
		method: "PUT",
	});
}

export const getMarketListings = () => {
	return requestLegacy<MarketListingResource[]>({
		url: urls.marketListings(),
		method: "GET",
	});
}

export const getMarketReports = () => {
    return requestLegacy<MarketReportResource[]>({
        url: urls.marketReports(),
        method: "GET",
    });
}

export const updateMarketReport = (marketReport: MarketReport, marketId: number) => {
	const body: MarketReportResource = {
		id: marketReport.id,
		typeId: marketReport.type.id,
		customUrl: marketReport.customUrl,
		displayInNavigation: marketReport.displayInNavigation,
		emailIntroText: marketReport.emailIntroText,
		webpageIntroText: marketReport.webpageIntroText,
		marketId: marketId,
		inactive: marketReport.inactive,
		limitEmailToFeatured: marketReport.limitEmailToFeatured,
	}
    return request<MarketReportResource, MarketReportResource>({
        url: urls.marketReport(body.id),
        method: "PUT",
        body,
    });
}

export const getMarketReportSubscriptions = () => {
    return requestLegacy<MarketReportSubscriptionResource[]>({
        url: urls.marketReportSubscriptions(),
        method: "GET",
    });
}

export const createMarketReportSubscription = (resource: NewMarketReportSubscription) => {
    const body: NewMarketReportSubscriptionResource = {
		marketReportId: resource.report.id,
		leadId: resource.lead.id,
		inactive: resource.inactive,
	}
    return request<MarketReportSubscriptionResource, NewMarketReportSubscriptionResource>({
        url: urls.marketReportSubscriptions(),
        method: "POST",
        body,
    });
}

export const updateMarketReportSubscription = (resource: MarketReportSubscription) => {
	const body: MarketReportSubscriptionResource = {
		id: resource.id,
		marketReportId: resource.report.id,
		leadId: resource.lead.id,
		inactive: resource.inactive,
		createdOn: resource.createdOn ? resource.createdOn.toISOString() : undefined,
	}
    return request<MarketReportSubscriptionResource, MarketReportSubscriptionResource>({
        url: urls.marketReportSubscription(body.id),
        method: "PUT",
        body,
    });
}

export const updateMarketSubscriptions = (marketId: number, subscriptions: (NewMarketReportSubscription | MarketReportSubscription)[]) => {
	let body: (NewMarketReportSubscriptionResource | MarketReportSubscriptionResource)[] = []; 
	subscriptions.forEach((subscription) => {
		if ("id" in subscription) {
			body.push({
				id: subscription.id,
				marketReportId: subscription.report.id,
				leadId: subscription.lead.id,
				inactive: subscription.inactive
			})
		} else {
			body.push({
				leadId: subscription.lead.id,
				marketReportId: subscription.report.id,
				inactive: subscription.inactive
			})
		}
	})
    return request<MarketReportSubscriptionResource[], (NewMarketReportSubscriptionResource | MarketReportSubscriptionResource)[]>({
        url: urls.marketSubscriptions(marketId),
        method: "PUT",
        body,
	});
}

export const getTasks = () => {
    return requestLegacy<TaskResource[]>({
        url: urls.tasks(),
        method: "GET",
    });
}

export const createTask = (task: NewTask) => {
	const body: NewTaskResource = {
        leadId: task.leadId,
		agentId: task.agentId,
		creatorId: task.creatorId,
        title: task.title,
        description: task.description,
        taskTypeId: task.type && task.type.id,
        taskStatusTypeId: task.status && task.status.id,
        priority: task.priority,
        startOn: task.startOn && task.startOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        endOn: task.endOn && task.endOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    };
	return request<TaskResource, NewTaskResource>({
		url: urls.tasks(),
		method: "POST",
		body,
	});
}

export const updateTask = (task: Task) => {
	const body: TaskResource = {
		id: task.id,
        leadId: task.leadId,
		agentId: task.agentId,
		creatorId: task.creatorId,
        title: task.title,
        description: task.description,
        taskTypeId: task.type && task.type.id,
        taskStatusTypeId: task.status && task.status.id,
        priority: task.priority,
        startOn: task.startOn && task.startOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        endOn: task.endOn && task.endOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		createdOn: task.createdOn && task.createdOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
		updatedOn: task.updatedOn && task.updatedOn.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    };
	return request<TaskResource, TaskResource>({
		url: urls.task(task.id),
		method: "PUT",
		body,
	});
}

export const getAgents = () => {
    return requestLegacy<AgentResource[]>({
        url: urls.agents(),
        method: "GET",
    });
}

export const createAgent = (agent: NewAgent) => {
	const body: NewAgentResource = {
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        officeId: agent.office && agent.office.id,
        agentPageUrl: agent.agentPageUrl,
        lastLoginDate:
            agent.lastLoginDate &&
            agent.lastLoginDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        designation: agent.designation,
        username: agent.username,
        password: agent.password,
        homePhone: agent.homePhone,
        officePhone: agent.officePhone,
        homeFax: agent.homeFax,
        officeFax: agent.officeFax,
        cellPhone: agent.cellPhone,
        staffTypeId: agent.staffType && agent.staffType.id,
        code: agent.code,
        officeAddress: agent.officeAddress,
        officeCity: agent.officeCity,
        officeState: agent.officeState,
        officeZip: agent.officeZip,
        photoUrl: agent.photoUrl,
        head: agent.head,
        body: agent.body,
        allowModifyBio: agent.allowModifyBio,
        hasOwnSite: agent.hasOwnSite,
        redirectUrl: agent.redirectUrl,
        enableAgentLink: agent.enableAgentLink,
        showOnSignupDropDown: agent.showOnSignupDropDown,
        emailHeader: agent.emailHeader,
        sendEmailUpdatesToAgent: agent.sendEmailUpdatesToAgent,
        allowModifyListings: agent.allowModifyListings,
        allowModifySubscribers: agent.allowModifySubscribers,
        languageIds: agent.languageIds,
        admin: agent.admin,
		ownsLeads: agent.ownsLeads,
		sortOrder: agent.sortOrder
    };
	return request<AgentResource, NewAgentResource>({
		url: urls.agents(),
		method: "POST",
		body,
	});
};

export const updateAgent = (agent: Agent) => {
	const body: AgentResource = {
        id: agent.id,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        officeId: agent.office && agent.office.id,
        agentPageUrl: agent.agentPageUrl,
        lastLoginDate:
            agent.lastLoginDate &&
            agent.lastLoginDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        designation: agent.designation,
        username: agent.username,
        password: agent.password,
        homePhone: agent.homePhone,
        officePhone: agent.officePhone,
        homeFax: agent.homeFax,
        officeFax: agent.officeFax,
        cellPhone: agent.cellPhone,
        staffTypeId: agent.staffType && agent.staffType.id,
        code: agent.code,
        officeAddress: agent.officeAddress,
        officeCity: agent.officeCity,
        officeState: agent.officeState,
        officeZip: agent.officeZip,
        photoUrl: agent.photoUrl,
        head: agent.head,
        body: agent.body,
        allowModifyBio: agent.allowModifyBio,
        hasOwnSite: agent.hasOwnSite,
        redirectUrl: agent.redirectUrl,
        enableAgentLink: agent.enableAgentLink,
        showOnSignupDropDown: agent.showOnSignupDropDown,
        emailHeader: agent.emailHeader,
        sendEmailUpdatesToAgent: agent.sendEmailUpdatesToAgent,
        allowModifyListings: agent.allowModifyListings,
        allowModifySubscribers: agent.allowModifySubscribers,
        languageIds: agent.languageIds,
        admin: agent.admin,
		ownsLeads: agent.ownsLeads,
		sortOrder: agent.sortOrder,
		showImageOnPage: agent.showImageOnPage,
		splashScreenSkipDate: agent.splashScreenSkipDate,
		publicBio: agent.publicBio ? {
			showHeader: agent.publicBio.showHeader,
			pageHeaderText: agent.publicBio.pageHeaderText,
			heroImage: agent.publicBio.heroImage ? { url: agent.publicBio.heroImage.url } : null,
			quickSearch: agent.publicBio.quickSearch,
			logoImage: agent.publicBio.logoImage ? { url: agent.publicBio.logoImage.url } : null,
			linkedinUrl: agent.publicBio.linkedinUrl,
			facebookUrl: agent.publicBio.facebookUrl,
			instagramUrl: agent.publicBio.instagramUrl,
			twitterUrl: agent.publicBio.twitterUrl,
			tiktokUrl: agent.publicBio.tiktokUrl,
			aboutBody: agent.publicBio.aboutBody,
			markets: agent.publicBio.markets ?? [],
		} : undefined,
    };
	return request<AgentResource, AgentResource>({
		url: urls.agent(agent.id),
		method: "PUT",
		body,
	});
};

export const updateSplashScreenSettings = ({ id, splashScreenSkipDate }: { id: number; splashScreenSkipDate: string }) => {
	const body: { id: number; splashScreenSkipDate: string | null } = {
		id,
		splashScreenSkipDate,
	};
	return request<AgentResource, { id: number; splashScreenSkipDate: string | null }>({
		url: urls.agent(id),
		method: "PUT",
		body,
	});
}

export const getAgentTestimonials = (agentId: number) => {
	return request<AgentPublicBioTestimonialResource[]>({
		url: urls.agentTestimonials(agentId),
		method: "GET",
	});
};

export const createAgentTestimonial = (agentId: number, testimonial: { quote: string; author: string }) => {
	return request<AgentPublicBioTestimonialResource, { quote: string; author: string }>({
		url: urls.agentTestimonials(agentId),
		method: "POST",
		body: testimonial,
	});
};

export const updateAgentTestimonial = (agentId: number, testimonialId: number, testimonial: { quote: string; author: string }) => {
	return request<AgentPublicBioTestimonialResource, { quote: string; author: string }>({
		url: urls.agentTestimonial(agentId, testimonialId),
		method: "PUT",
		body: testimonial,
	});
};

export const deleteAgentTestimonial = (agentId: number, testimonialId: number) => {
	return request<void, void>({
		url: urls.agentTestimonial(agentId, testimonialId),
		method: "DELETE",
	});
};

export const updateAgents = (agents: Agent[]) => { 
	const body: AgentResource[] = [];
	agents.forEach((agent) => {
		body.push({
			id: agent.id,
			firstName: agent.firstName,
			lastName: agent.lastName,
			email: agent.email,
			officeId: agent.office && agent.office.id,
			agentPageUrl: agent.agentPageUrl,
			lastLoginDate:
				agent.lastLoginDate &&
				agent.lastLoginDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
			designation: agent.designation,
			username: agent.username,
			password: agent.password,
			homePhone: agent.homePhone,
			officePhone: agent.officePhone,
			homeFax: agent.homeFax,
			officeFax: agent.officeFax,
			cellPhone: agent.cellPhone,
			staffTypeId: agent.staffType && agent.staffType.id,
			code: agent.code,
			officeAddress: agent.officeAddress,
			officeCity: agent.officeCity,
			officeState: agent.officeState,
			officeZip: agent.officeZip,
			photoUrl: agent.photoUrl,
			head: agent.head,
			body: agent.body,
			allowModifyBio: agent.allowModifyBio,
			hasOwnSite: agent.hasOwnSite,
			redirectUrl: agent.redirectUrl,
			enableAgentLink: agent.enableAgentLink,
			showOnSignupDropDown: agent.showOnSignupDropDown,
			emailHeader: agent.emailHeader,
			sendEmailUpdatesToAgent: agent.sendEmailUpdatesToAgent,
			allowModifyListings: agent.allowModifyListings,
			allowModifySubscribers: agent.allowModifySubscribers,
			languageIds: agent.languageIds,
			admin: agent.admin,
			ownsLeads: agent.ownsLeads,
			showImageOnPage: agent.showImageOnPage,
		});
	})
	return request<AgentResource[], AgentResource[]>({
		url: urls.agents(),
		method: "PUT",
		body,
	});
} 

export const deleteAgent = (agent: Agent) => {
    return requestLegacy<AgentResource>({
        url: urls.agent(agent.id || 0),
        method: "DELETE",
    })
}

export const getBoards = () => {
    return requestLegacy<BoardResource[]>({
        url: urls.boards(),
        method: "GET",
    });
};

export const getAgentBoards = () => {
    return request<AgentBoardResource[]>({
        url: urls.agentBoards(),
        method: "GET",
    })
}

export const createAgentBoard = (agentBoard: NewAgentBoard) => {
    const body: NewAgentBoardResource = {
        agentCode: agentBoard.agentCode,
        agentId: agentBoard.agentId,
        boardId: agentBoard.boardId,
    };
    return request<AgentBoardResource, NewAgentBoardResource>({
        url: urls.agentBoards(),
        method: "POST",
        body,
    });
};

export const updateAgentBoard = (agentBoard: AgentBoard) => {
    const body: AgentBoardResource = {
        id: agentBoard.id,
        agentCode: agentBoard.agentCode,
        agentId: agentBoard.agentId,
        boardId: agentBoard.boardId,
    };
    return request<AgentBoardResource, AgentBoardResource>({
        url: urls.agentBoard(agentBoard.id),
        method: "PUT",
        body,
    });
};

// export const saveAgentBoard = (agentBoard: AgentBoard) => {
//  const body: AgentBoardResource = {
//      id: agentBoard.id,
//      agentCode: agentBoard.agentCode,
//      agentId: agentBoard.agentId,
//      boardId: agentBoard.boardId,
//  }
//  if (agentBoard.id) {
//      return requestLegacy<AgentBoardResource>({
//          url: urls.agentBoard(agentBoard.id),
//          method: "PUT",
//          body,
//      });
//  } else {
//      return requestLegacy<AgentBoardResource>({
//          url: urls.agentBoards(),
//          method: "POST",
//          body,
//      })
//  }
// }

export const deleteAgentBoard = (agentBoard: AgentBoard) => {
    return requestLegacy<AgentBoardResource>({
        url: urls.agentBoard(agentBoard.id || 0),
        method: "DELETE",
    })
}

export const getAgentMlsIds = () => {
    return request<AgentMlsIdResource[]>({
        url: urls.agentMlsIds(),
        method: "GET",
    });
}

// A write the MLS ID rules refuse is a 409 whose message is the rule's code; the caller words it. A create answers
// 201 with the new entry, and a delete or Remove All answers 204 with no body.
export const createAgentMlsId = (agentMlsId: NewAgentMlsIdResource) => {
    return request<AgentMlsIdResource, NewAgentMlsIdResource>({
        url: urls.agentMlsIds(),
        method: "POST",
        body: agentMlsId,
    });
}

export const updateAgentMlsId = (id: number, agentMlsId: NewAgentMlsIdResource) => {
    return request<AgentMlsIdResource, NewAgentMlsIdResource>({
        url: urls.agentMlsId(id),
        method: "PUT",
        body: agentMlsId,
    });
}

export const deleteAgentMlsId = (id: number) => {
    return request<void>({
        url: urls.agentMlsId(id),
        method: "DELETE",
    });
}

export const getClientBoards = () => {
    return requestLegacy<ClientBoardListResource>({
        url: urls.clientBoards(),
        method: "GET",
    });
}

export const updateClientBoard = (id: number, clientBoard: ClientBoardResource, strict = false) => {
    return request<ClientBoardResource, ClientBoardResource>({
        url: urls.clientBoard(id, strict),
        method: "PUT",
        body: clientBoard,
    });
}

export const addClientBoard = (clientBoard: ClientBoardResource, strict = false) => {
    return request<ClientBoardResource, ClientBoardResource>({
        url: urls.clientBoards(strict),
        method: "POST",
        body: clientBoard,
    });
}

export const getClientBoardOptions = () => {
    return requestLegacy<BoardOption[]>({
        url: urls.clientBoardOptions(),
        method: "GET",
    });
}

export const getOffices = () => {
    return requestLegacy<OfficeResource[]>({
        url: urls.offices(),
        method: "GET",
    });
}

/**
 * Persists the offices' custom display order. Only ids are sent — the server renumbers
 * BrokerOffices.Priority densely from the array order (OfficeService.reorderOffices), so
 * sending stale field values here would risk clobbering concurrent edits.
 */
export const updateOffices = (offices: Office[]) => {
	const body: OfficeResource[] = offices.map((office) => ({ id: office.id }));
	return request<OfficeResource[], OfficeResource[]>({
		url: urls.offices(),
		method: "PUT",
		body,
	});
};

export const createOffice = (office: NewOffice) => {
	const body: NewOfficeResource = {
		name: office.name,
		code: office.code,
		priority: office.priority,
		address: office.address,
		city: office.city,
		state: office.state,
		zip: office.zip,
		phone: office.phone,
		fax: office.fax,
		email: office.email,
		photoUrl: office.photoUrl,
		header: office.header,
		description: office.description,
	};
	return request<OfficeResource, NewOfficeResource>({
		url: urls.offices(),
		method: "POST",
		body,
	});
};

export const updateOffice = (office: Office) => {
	const body: OfficeResource = {
		id: office.id,
        name: office.name,
        code: office.code,
        priority: office.priority,
        address: office.address,
        city: office.city,
        state: office.state,
        zip: office.zip,
        phone: office.phone,
        fax: office.fax,
        email: office.email,
        photoUrl: office.photoUrl,
        header: office.header,
        description: office.description,
	};
	return request<OfficeResource, OfficeResource>({
		url: urls.office(office.id),
		method: "PUT",
		body,
	});
};

export const deleteOffice = (office: Office) => {
    return requestLegacy<OfficeResource>({
        url: urls.office(office.id || 0),
        method: "DELETE",
    })
}

export const getOfficeBoards = () => {
    return requestLegacy<OfficeBoardResource[]>({
        url: urls.officeBoards(),
        method: "GET",
    })
}

export const createOfficeBoard = (officeBoard: NewOfficeBoard) => {
	const body: NewOfficeBoardResource = {
		boardId: officeBoard.boardId,
		officeCode: officeBoard.officeCode,
		officeId: officeBoard.officeId,
	}
	return request<OfficeBoardResource, NewOfficeBoardResource>({
		url: urls.officeBoards(),
		method: "POST",
		body,
	})
};

export const updateOfficeBoard = (officeBoard: OfficeBoard) => {
	const body: OfficeBoardResource = {
		id: officeBoard.id,
		boardId: officeBoard.boardId,
		officeCode: officeBoard.officeCode,
		officeId: officeBoard.officeId,
	}
	return request<OfficeBoardResource, OfficeBoardResource>({
		url: urls.officeBoard(officeBoard.id),
		method: "PUT",
		body,
	})
};

export const deleteOfficeBoard = (officeBoard: OfficeBoard) => {
    return requestLegacy<OfficeBoardResource>({
        url: urls.officeBoard(officeBoard.id || 0),
        method: "DELETE",
    })
}

export const getCities = () => {
    return requestLegacy<CityResource[]>({
        url: urls.cities(),
        method: "GET",
    });
}

export const getNeighborhoods = () => {
    return requestLegacy<NeighborhoodResource[]>({
        url: urls.neighborhoods(),
        method: "GET",
    });
}

export const getAggregateStats = () => {
    return requestLegacy<AggregateStatsResource[]>({
        url: urls.aggregateStats(),
        method: "GET",
    });
}

export const getIntegrations = () => {
    // Fetch both account-level and agent-level (ownerTypeId=2) integrations
    return Promise.all([
        requestLegacy<IntegrationResource[]>({
            url: urls.integrations(),
            method: "GET",
        }),
        requestLegacy<IntegrationResource[]>({
            url: urls.integrations(2), // Agent-level integrations (Facebook, etc.)
            method: "GET",
        }),
    ]).then(([accountLevel, agentLevel]) => {
        // Combine both arrays and deduplicate by ID
        const combined = [...accountLevel, ...agentLevel];
        const uniqueById = combined.reduce((acc, integration) => {
            if (integration.id) {
                acc[integration.id] = integration;
            }
            return acc;
        }, {} as Record<number, IntegrationResource>);
        return Object.values(uniqueById);
    });
}

export const saveIntegration = (integration: IntegrationPartial) => {
    const body: IntegrationResource = {
        id: integration.id,
        integrationTypeId: integration.integrationType.id,
        name: integration.name,
        data: integration.data,
    };
    if (integration.id) {
        return requestLegacy<IntegrationResource>({
            url: urls.integration(integration.id),
            method: "PUT",
            body,
        });
    } else {
        return requestLegacy<IntegrationResource>({
            url: urls.integrations(),
            method: "POST",
            body
        });
    }
}

export const getIntegrationCalendars = (integrationId: number) => {
    return requestLegacy<IntegrationCalendarResource[]>({
        url: urls.integrationCalendars(integrationId),
        method: "GET",
    });
}

export const getFacebookPages = () => {
    return requestLegacy<FacebookPagesResource>({
        url: urls.facebookPages(),
        method: "GET",
    });
}

export const deleteIntegration = (integration: IntegrationPartial) => {
    return requestLegacy<IntegrationResource>({
        url: urls.integration(integration.id || 0),
        method: "DELETE",
    });
}

export const testIntegration = (integration: IntegrationPartial) => {
    return requestLegacy<IntegrationResource>({
        url: urls.integrationTest(integration.id || 0),
        method: "POST",
    });
}

export const connectFollowUpBoss = (integrationId: number, apiKey: string) => {
    return requestLegacy<{ message?: string }>({
        url: urls.followUpBossConnect(integrationId),
        method: "POST",
        body: { apiKey } as any,
    });
};

export const disconnectFollowUpBoss = (integrationId: number) => {
    return requestLegacy<{ message?: string }>({
        url: urls.followUpBossDisconnect(integrationId),
        method: "POST",
    });
};

export const getFollowUpBossStatus = (integrationId: number) => {
    return requestLegacy<{
        connected: boolean;
        webhooksActive: number;
        webhooksMissing: number;
        webhookLastHealthCheckDate: number | null;
        agentsMatched: number | null;
        ihfAgentsNotInFub: number | null;
        fubUsersNotInIhf: number | null;
        backfillStatus: "not_started" | "running" | "complete" | "failed" | null;
        apiKeyValid: boolean | null;
        lastWebhookProcessed: number | null;
        lastSyncDate: number | null;
        errorCount24h: number;
        backfillLeadsStatus: string | null;
        backfillEventsStatus: string | null;
        leadsMatched: number | null;
        leadsUnmatched: number | null;
    }>({
        url: urls.followUpBossStatus(integrationId),
        method: "GET",
    });
};

export const startFollowUpBossBackfill = (integrationId: number) => {
    return requestLegacy<{}>({
        url: urls.followUpBossStartBackfill(integrationId),
        method: "POST",
    });
};

// MAX-4236: agent selection. A non-2xx carries { message } which request() surfaces as the Error message.
export const getFollowUpBossAgentSelection = (integrationId: number) => {
    return requestLegacy<FollowUpBossAgentSelection>({
        url: urls.followUpBossAgentSelection(integrationId),
        method: "GET",
    });
};

export const saveFollowUpBossAgentSelection = (integrationId: number, body: FollowUpBossAgentSelectionRequest) => {
    return requestLegacy<FollowUpBossAgentSelection>({
        url: urls.followUpBossAgentSelection(integrationId),
        method: "PUT",
        body: body as any,
    });
};


export const getSiteLinks = () => {
    return requestLegacy<SiteLinkResource[]>({
        url: urls.siteLinks(),
        method: "GET"
    });
}

export const sendEmail = (label: string, email: string, subject: string, body: string) => {
    return requestLegacy({
        url: urls.sendEmail(email, label, subject, body),
        method: "POST",
    });
}

export const getUploadSignature = (uploadPreset: string) => {
    return requestLegacy<string>({
        url: urls.uploadSignature(uploadPreset),
        method: "GET",
    })
}

export const getGeocode = (address?: string) => {
    return requestLegacy<[ number, number ]>({
        url: urls.geocode(address),
        method: "GET",
    })
}

export const getPages = () => {
    return requestLegacy<PageResource[]>({
        url: urls.pages(),
        method: "GET",
    });
}

export const savePage = (page: PagePartial) => {
    const body: PageResource = {
        id: page.id,
        typeId: page.type.id,
        path: page.path,
        title: page.title,
        head: page.head,
        enabled: page.enabled,
    };
    return requestLegacy<PageResource>({
        url: urls.page(page.id),
        method: "PUT",
        body,
    });
}

export const getLanguages = () => {
    return requestLegacy<LanguageResource[]>({
        url: urls.languages(),
        method: "GET",
    });
}

export const exchangeNylasToken = async (obj: NylasTokenRequest): Promise<NylasTokenResponse> => {
	try {
		const body: NylasTokenRequest = {
			grant_type: 'authorization_code',
			code_verifier: 'nylas',
			...obj,
		}
		const request = await fetch('https://api.us.nylas.com/v3/connect/token', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(body),
		});
		if (!request.ok) {
			const errorData = await request.json();
			throw new Error(errorData.error_description || 'Failed to exchange token');
		}
		const data = await request.json();
		return data as NylasTokenResponse;
	} catch(error) {
		if (error instanceof Error) {
			throw new Error(`Failed to exchange Nylas token: ${error.message}`);
		}
		throw new Error('An unexpected error occurred while exchanging Nylas token');
	}
}

export const getNaturalLanguageSearchSuggestions = () => {
	return request<NaturalLanguageSearchSuggestionResource[]>({
		url: urls.naturalLanguageSearchSuggestions(),
		method: "GET",
	});
}

export const createNaturalLanguageSearchSuggestion = (
	suggestion: NewNaturalLanguageSearchSuggestion
) => {
	const body: NewNaturalLanguageSearchSuggestionResource = {
		nls: suggestion.nls,
		promoted: suggestion.promoted,
	};
	return request<NaturalLanguageSearchSuggestionResource, NewNaturalLanguageSearchSuggestionResource>({
		url: urls.naturalLanguageSearchSuggestions(),
		method: "POST",
		body,
	});
}

export const updateNaturalLanguageSearchSuggestion = (
	suggestion: NaturalLanguageSearchSuggestion
) => {
	const body: NaturalLanguageSearchSuggestionResource = {
		id: suggestion.id,
		nls: suggestion.nls,
		promoted: suggestion.promoted,
	};
	return request<NaturalLanguageSearchSuggestionResource, NaturalLanguageSearchSuggestionResource>({
		url: urls.naturalLanguageSearchSuggestion(suggestion.id),
		method: "PUT",
		body,
	});
}

export const deleteNaturalLanguageSearchSuggestion = (id: number) => {
	return request<void>({
		url: urls.naturalLanguageSearchSuggestion(id),
		method: "DELETE",
	});
}

export const getAreaSelectorPlaceholders = () => {
	return request<AreaSelectorPlaceholderResource[]>({
		url: urls.areaSelectorPlaceholders(),
		method: "GET",
	});
}

export const createAreaSelectorPlaceholder = (
	placeholder: NewAreaSelectorPlaceholder
) => {
	const body: NewAreaSelectorPlaceholderResource = {
		text: placeholder.text,
	};
	return request<AreaSelectorPlaceholderResource, NewAreaSelectorPlaceholderResource>({
		url: urls.areaSelectorPlaceholders(),
		method: "POST",
		body,
	});
}

export const updateAreaSelectorPlaceholder = (
	placeholder: AreaSelectorPlaceholder
) => {
	const body: AreaSelectorPlaceholderResource = {
		id: placeholder.id,
		text: placeholder.text,
	};
	return request<AreaSelectorPlaceholderResource, AreaSelectorPlaceholderResource>({
		url: urls.areaSelectorPlaceholder(placeholder.id),
		method: "PUT",
		body,
	});
}

export const deleteAreaSelectorPlaceholder = (id: number) => {
	return request<void>({
		url: urls.areaSelectorPlaceholder(id),
		method: "DELETE",
	});
}

export const getEmailRoutingRules = () => {
	return request<EmailRoutingRuleResource[]>({
		url: urls.emailRoutingRules(),
		method: "GET",
	});
}

export const bulkUpsertEmailRoutingRules = (body: UpsertEmailRoutingRuleResource[]) => {
	return request<EmailRoutingRuleResource[], UpsertEmailRoutingRuleResource[]>({
		url: urls.emailRoutingRules(),
		method: "PUT",
		body,
	});
}

export const getEmailRecipients = () => {
	return request<EmailRecipientResource[]>({
		url: urls.emailRecipients(),
		method: "GET",
	});
}

export const addEmailRecipients = (body: CreateEmailRecipientResource[]) => {
	return request<EmailRecipientResource[], CreateEmailRecipientResource[]>({
		url: urls.emailRecipients(),
		method: "POST",
		body,
	});
}

export const deleteEmailRecipient = (clientEmailRecipientId: number) => {
	return request<void>({
		url: urls.emailRecipient(clientEmailRecipientId),
		method: "DELETE",
	});
}

export const getOffMarketListings = () => {
	return request<OffMarketListingListResource>({
		url: urls.offMarketListings(),
		method: "GET",
	});
}

export const createOffMarketListing = (body: UpsertOffMarketListingResource) => {
	return request<OffMarketListingResource, UpsertOffMarketListingResource>({
		url: urls.offMarketListings(),
		method: "POST",
		body,
	});
}

export const updateOffMarketListing = (id: number, body: UpsertOffMarketListingResource) => {
	return request<OffMarketListingResource, UpsertOffMarketListingResource>({
		url: urls.offMarketListing(id),
		method: "PUT",
		body,
	});
}

export const deleteOffMarketListing = (id: number) => {
	return request<void>({
		url: urls.offMarketListing(id),
		method: "DELETE",
	});
}

export const getClientProperties = () => {
	return request<ClientPropertyListResource>({
		url: urls.clientProperties(),
		method: "GET",
	});
}

export const getClientProperty = (id: number) => {
	return request<ClientPropertyResource>({
		url: urls.clientProperty(id),
		method: "GET",
	});
}

export const createClientProperty = (body: UpsertClientPropertyResource) => {
	return request<ClientPropertyResource, UpsertClientPropertyResource>({
		url: urls.clientProperties(),
		method: "POST",
		body,
	});
}

export const updateClientProperty = (id: number, body: UpsertClientPropertyResource) => {
	return request<ClientPropertyResource, UpsertClientPropertyResource>({
		url: urls.clientProperty(id),
		method: "PUT",
		body,
	});
}

export const deleteClientProperty = (id: number) => {
	return request<void>({
		url: urls.clientProperty(id),
		method: "DELETE",
	});
}

export const getListingPhotos = (key: ListingPhotoKey) => {
	return request<PhotoManagerResource>({
		url: urls.listingPhotos(key),
		method: "GET",
	});
}

export const addListingPhoto = (key: ListingPhotoKey, body: AddListingPhotoResource) => {
	return request<PhotoManagerResource, AddListingPhotoResource>({
		url: urls.listingPhotos(key),
		method: "POST",
		body,
	});
}

export const reorderListingPhotos = (key: ListingPhotoKey, body: ReorderListingPhotosResource) => {
	return request<PhotoManagerResource, ReorderListingPhotosResource>({
		url: urls.listingPhotosReorder(key),
		method: "PUT",
		body,
	});
}

export const deleteListingPhoto = (propertyPhotoId: number, key: ListingPhotoKey) => {
	return request<void>({
		url: urls.listingPhoto(propertyPhotoId, key),
		method: "DELETE",
	});
}

export const makeMainListingPhoto = (key: ListingPhotoKey, body: MakeMainListingPhotoResource) => {
	return request<PhotoManagerResource, MakeMainListingPhotoResource>({
		url: urls.listingPhotosMain(key),
		method: "POST",
		body,
	});
}

export const revertListingPhotosToMls = (key: ListingPhotoKey) => {
	return request<PhotoManagerResource>({
		url: urls.listingPhotosRevert(key),
		method: "POST",
	});
}

export const getLeadContactReport = (params: LeadContactReportQuery) => {
	return request<LeadContactReportResource>({
		url: urls.leadContactReport(params),
		method: "GET",
	});
}

export const getContactEventTypes = () => {
	return request<ContactEventTypeResource[]>({
		url: urls.contactEventTypes(),
		method: "GET",
	});
}

export const getSavedSearchActivity = (params: SavedSearchActivityQuery) => {
	return request<SavedSearchActivityReportResource>({
		url: urls.savedSearchActivity(params),
		method: "GET",
	});
}

export const getSavedListingsReport = (startDate?: string, endDate?: string) => {
	return request<SavedListingReportResource[]>({
		url: urls.savedListingsReport(startDate, endDate),
		method: "GET",
	});
}

export const getMobileActivity = (params: MobileActivityQuery) => {
	return request<MobileActivityReportResponse>({
		url: urls.mobileActivity(params),
		method: "GET",
	});
}

// The control-panel API authenticates via a Basic header built from stored credentials + X-Source
// (see `request` above) — NOT cookies. Raw fetches that bypass `request` (blob/HTML responses) must
// replicate those headers or they 401.
const controlPanelAuthHeader = (): string => {
	const creds = credentials.get();
	let auth = ":";
	if (creds) {
		const { username, password, clientId } = creds;
		auth = clientId ? `${username}|${clientId}:${password}` : `${username}:${password}`;
	}
	return "Basic " + btoa(auth);
};

// CSV blob download (MAX-3881). `request` parses JSON, so we fetch directly, read the blob, and
// trigger a client-side download of SubscriberStats.csv. Non-200 rejects so the caller can notify.
export const exportLeads = async (orderBy: string, includeDisabled: boolean, includeDeleted: boolean) => {
	await downloadCsv(urls.leadsExport({ orderBy, includeDisabled, includeDeleted }), "SubscriberStats.csv");
};

// Team export (MAX-4455), replacing the unauthenticated link to CF agent_export.cfm.
export const exportAgents = async () => {
	await downloadCsv(urls.agentsExport(), "AgentStats.csv");
};

const downloadCsv = async (exportUrl: string, filename: string) => {
	const response = await fetch(exportUrl, {
		method: "GET",
		headers: {
			"Authorization": controlPanelAuthHeader(),
			"X-Source": "controlPanel",
		},
	});
	if (!response.ok) {
		throw new Error(`Export failed (${response.status})`);
	}
	saveBlob(await response.blob(), filename);
};

export const getActiveListings = (params: { agentId?: number; featuredOnlyYn?: boolean; activeFeaturedForReport?: boolean; context?: string }) => {
	return request<ActiveListingResource[]>({
		url: urls.activeListings(params),
		method: "GET",
	});
}

export const getActiveListingModify = (params: { listingNumber: string; boardId: number }) => {
	return request<ActiveListingModifyResource>({
		url: urls.activeListingModify(params),
		method: "GET",
	});
}

export const updateActiveListingModify = (
	params: { listingNumber: string; boardId: number },
	body: UpdateActiveListingModifyResource
) => {
	return request<ActiveListingModifyResource, UpdateActiveListingModifyResource>({
		url: urls.activeListingModify(params),
		method: "PUT",
		body,
	});
}

export const getEmailBranding = () => {
	return request<EmailBrandingResource>({
		url: urls.emailBranding(),
		method: "GET",
	});
}

export const updateEmailBranding = (body: EmailBrandingResource) => {
	return request<EmailBrandingResource, EmailBrandingResource>({
		url: urls.emailBranding(),
		method: "PUT",
		body,
	});
}

// Preview returns rendered text/html (not JSON), so fetch directly with the CP auth headers.
export const previewEmailBranding = async (body: EmailBrandingResource) => {
	const response = await fetch(urls.emailBrandingPreview(), {
		method: "POST",
		headers: {
			"Authorization": controlPanelAuthHeader(),
			"Content-Type": "application/json",
			"X-Source": "controlPanel",
		},
		body: JSON.stringify(body),
	});
	if (!response.ok) {
		throw new Error(`Preview failed (${response.status})`);
	}
	return response.text();
};

// Billing answers 409 (ineligible account), 422 (card declined) and 429 (throttled) as ordinary
// outcomes the UI has copy for, so those are not reported as JS errors. 5xx and network failures are.
const BILLING_EXPECTED_STATUSES = [409, 422, 429];

export const getBilling = () => {
	return request<BillingResource>({
		url: urls.billing(),
		method: "GET",
		expectedStatuses: BILLING_EXPECTED_STATUSES,
	});
}

export const updateBillingCard = (token: string) => {
	return request<BillingCardResource, BillingCardUpdateResource>({
		url: urls.billingCard(),
		method: "PUT",
		body: { token },
		expectedStatuses: BILLING_EXPECTED_STATUSES,
	});
}

export const getBillingInvoices = () => {
	return request<BillingInvoiceResource[]>({
		url: urls.billingInvoices(),
		method: "GET",
		expectedStatuses: BILLING_EXPECTED_STATUSES,
	});
}

// PDF blob download, same shape as exportLeads. No Accept header: the endpoint only produces
// application/pdf, so a JSON Accept would be refused with a 406.
export const downloadBillingInvoice = async (displayNumber: string) => {
	const response = await fetch(urls.billingInvoicePdf(displayNumber), {
		method: "GET",
		headers: {
			"Authorization": controlPanelAuthHeader(),
			"X-Source": "controlPanel",
		},
	});
	if (!response.ok) {
		const error = new Error(`Invoice download failed (${response.status})`) as Error & { status?: number };
		error.status = response.status;
		throw error;
	}
	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `invoice-${displayNumber}.pdf`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

interface requestLegacyOptions<T> {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    body?: T;
    /** Statuses the caller handles as ordinary outcomes: still rejected, but not reported to New Relic. */
    expectedStatuses?: number[];
}

const requestLegacy = <T>(options: requestLegacyOptions<T>): Promise<T> => {
    return request(options);
}

const request = <ResponseBody, RequestBody = undefined>(options: requestLegacyOptions<RequestBody>): Promise<ResponseBody> => {
    const { url, method, body, expectedStatuses } = options;
    return new Promise((resolve, reject) => {
        const creds = credentials.get();
        let auth = ":";
        if (creds) {
            let { username, password, clientId } = creds;
            if (clientId) {
                auth = username + "|" + clientId + ":" + password;
            } else {
                auth = username + ":" + password;
            }
        }
        fetch(url, {
            method: method,
            body: JSON.stringify(body),
            headers: {
                "Authorization": "Basic " + btoa(auth),
                "Content-Type": "application/json",
				"X-Source": "controlPanel",
            },
        }).then(
            async (response) => {
                if (response.status === 204) {
                    resolve(undefined as ResponseBody);
                    return;
                }
                if (!response.ok) {
                    let message: string | undefined;
                    let errorCode: string | undefined;
                    try {
                        const json = await response.json();
                        message = json && json.message;
                        errorCode = json && json.errorCode;
                    } catch{
                        message = undefined;
                    }
                    const error = new Error(message) as Error & { status?: number; code?: string };
                    error.status = response.status;
                    // `code` rather than a custom field: miniSerializeError keeps it through a rejected thunk.
                    if (errorCode) {
                        error.code = errorCode;
                    }
                    if (!expectedStatuses?.includes(response.status)) {
                        window.newrelic.noticeError(error);
                    }
                    reject(error);
                    return;
                }
                try {
                    const json = await response.json();
                    resolve(json);
                } catch(error) {
                    window.newrelic.noticeError(error as Error);
                    reject(error);
                }
            },
            (error) => {
                window.newrelic.noticeError(error);
                reject(error);
            }
        );
    });
}
